/**
 * <s-product-gallery> — one-image-at-a-time product slider.
 *
 * Swipe/scroll moves the native snap track; thumbnail and arrow buttons send
 * commands (--go, --prev, --next) to this element, which scrolls the track and
 * keeps the active thumbnail and the "n / total" counter in sync.
 *
 * Required markup:
 *   [data-gallery-track]        — horizontal scroll-snap container of slides.
 *   [data-gallery-thumb]        — button with commandfor=<this id>, command="--go", data-index.
 *   [data-gallery-counter]      — optional element receiving "n / total".
 */
class SProductGallery extends HTMLElement {
  #ac;
  #track;
  #index = 0;
  #frame = 0;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    this.#track = this.querySelector('[data-gallery-track]');
    if (!this.#track) return;

    this.addEventListener('command', this.#handleCommand, opts);
    this.#track.addEventListener('scroll', this.#handleScroll, { ...opts, passive: true });
    this.#sync();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
  }

  get #count() {
    return this.#track ? this.#track.children.length : 0;
  }

  #handleCommand = (event) => {
    const command = event.command;
    if (command === '--go') {
      this.#goTo(Number(event.source?.dataset.index || 0));
    } else if (command === '--next') {
      this.#goTo(this.#index + 1);
    } else if (command === '--prev') {
      this.#goTo(this.#index - 1);
    }
  };

  #goTo(index) {
    const count = this.#count;
    if (!count) return;
    const next = (index + count) % count;
    const slide = this.#track.children[next];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
    this.#index = next;
    this.#sync();
  }

  #handleScroll = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => {
      const width = this.#track.clientWidth || 1;
      // scrollLeft is negative in RTL documents; the magnitude gives the slide position.
      const index = Math.round(Math.abs(this.#track.scrollLeft) / width);
      if (index !== this.#index) {
        this.#index = Math.min(index, this.#count - 1);
        this.#sync();
      }
    });
  };

  #sync() {
    const count = this.#count;
    this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
      const active = Number(thumb.dataset.index) === this.#index;
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
    const counter = this.querySelector('[data-gallery-counter]');
    if (counter) counter.textContent = `${this.#index + 1} / ${count}`;
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
